// From useHover hook at usehooks.com by Gabe Ragland
// https://gist.github.com/gragland/a32d08580b7e0604ff02cb069826ca2f

import { useState, useCallback, useRef } from 'react';
import { BrowserAbilities } from 'javascripts/shared/utils/browser_abilities';

export function useHover() {
  const [value, setValue] = useState(false);

  // Keep track of the last node passed to callbackRef
  // so we can remove its event listeners.
  const ref = useRef();

  // Wrap in useCallback so we can use in dependencies below
  const handleMouseOver = useCallback(() => setValue(true), []);
  const handleMouseOut = useCallback(() => setValue(false), []);

  const handleBlur = useCallback(e => {
    if (ref.current && !(ref.current === e.relatedTarget || ref.current.contains(e.relatedTarget))) {
      setValue(false);
    }
  }, []);

  // Use a callback ref instead of useEffect so that event listeners
  // get changed in the case that the returned ref gets added to
  // a different element later. With useEffect, changes to ref.current
  // wouldn't cause a rerender and thus the effect would run again.
  const callbackRef = useCallback(
    node => {
      if (ref.current) {
        if (BrowserAbilities.hoverEnabled()) {
          ref.current.removeEventListener('mouseover', handleMouseOver);
          ref.current.removeEventListener('mouseout', handleMouseOut);
        }
        ref.current.removeEventListener('focus', handleMouseOver);
        ref.current.removeEventListener('blur', handleBlur);
      }

      ref.current = node;

      if (ref.current) {
        if (BrowserAbilities.hoverEnabled()) {
          ref.current.addEventListener('mouseover', handleMouseOver);
          ref.current.addEventListener('mouseout', handleMouseOut);
        }
        ref.current.addEventListener('focus', handleMouseOver);
        ref.current.addEventListener('blur', handleBlur);
      }
    },
    [handleMouseOver, handleMouseOut, handleBlur],
  );

  return [callbackRef, value];
}
